import React from "react";
import { DataGrid, GridToolbarQuickFilter, enUS } from "@mui/x-data-grid";
import { Box, Paper, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import { useTranslation } from "react-i18next";

import { NAV_BG, AMBER } from '../theme';

function CustomToolbar() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      <GridToolbarQuickFilter
        variant="outlined"
        size="small"
        placeholder={t('parents.search')}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontFamily: '"Outfit", sans-serif',
            fontSize: '0.85rem',
            borderRadius: '8px',
            background: '#f8fafc',
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: AMBER },
            '&.Mui-focused fieldset': { borderColor: AMBER, borderWidth: 1.5 },
          },
          '& .MuiInputBase-input': { py: 0.75 },
        }}
      />
    </Box>
  );
}

const ParentsComponent = ({ parents, onSelectionChange, onEdit, onDelete, onRestore }) => {
  const { t } = useTranslation();

  const columns = [
    {
      field: 'lastname',
      headerName: t('common.last_name'),
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Typography onClick={() => onEdit && onEdit(params.row)} sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: NAV_BG, fontWeight: 500, cursor: onEdit ? 'pointer' : 'default', '&:hover': onEdit ? { color: AMBER, textDecoration: 'underline' } : {} }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'firstname',
      headerName: t('common.first_name'),
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Typography onClick={() => onEdit && onEdit(params.row)} sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: NAV_BG, fontWeight: 500, cursor: onEdit ? 'pointer' : 'default', '&:hover': onEdit ? { color: AMBER, textDecoration: 'underline' } : {} }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'email',
      headerName: t('common.email'),
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Typography noWrap sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }} title={params.value}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'phone_number',
      headerName: t('common.phone_number'),
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#475569' }}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'children',
      headerName: t('parents.children_col'),
      flex: 2,
      minWidth: 200,
      sortable: false,
      valueGetter: (value) => Array.isArray(value) ? value.map(c => `${c.firstname} ${c.lastname}`).join(' ') : '',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', py: 0.5 }}>
          {params.row.children && params.row.children.length > 0 ? params.row.children.map((child) => (
            <Chip
              key={child.child_id}
              label={`${child.firstname} ${child.lastname}`}
              size="small"
              sx={{
                fontFamily: '"Outfit", sans-serif',
                fontSize: '0.75rem',
                background: 'rgba(15,45,94,0.07)',
                color: NAV_BG,
                fontWeight: 500,
                height: 22,
              }}
            />
          )) : (
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#94a3b8' }}>
              {t('parents.no_children')}
            </Typography>
          )}

        </Box>
      ),
    },
    {
      field: '_status',
      headerName: t('common.status'),
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const { is_deleted, is_registered } = params.row;
        if (is_deleted) {
          return <Chip label={t('parents.status_deleted')} size="small" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', background: '#fee2e2', color: '#b91c1c', fontWeight: 600, height: 22 }} />;
        }
        if (!is_registered) {
          return <Chip label={t('parents.status_inactive')} size="small" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', background: '#fef3c7', color: '#92400e', fontWeight: 600, height: 22 }} />;
        }
        return <Chip label={t('parents.status_active')} size="small" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', background: '#dcfce7', color: '#166534', fontWeight: 600, height: 22 }} />;
      },
    },
    {
      field: '_actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {onEdit && (
            <Tooltip title={t('common.edit')}>
              <IconButton size="small" onClick={() => onEdit(params.row)} sx={{ color: NAV_BG, opacity: 0.7, '&:hover': { opacity: 1 } }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && !params.row.is_deleted && (
            <Tooltip title={t('common.delete')}>
              <IconButton size="small" onClick={() => onDelete(params.row)} sx={{ color: '#b91c1c', opacity: 0.6, '&:hover': { opacity: 1 } }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onRestore && params.row.is_deleted && (
            <Tooltip title={t('common.restore')}>
              <IconButton size="small" onClick={() => onRestore(params.row)} sx={{ color: '#166534', opacity: 0.7, '&:hover': { opacity: 1 } }}>
                <RestoreFromTrashIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const rows = parents.map((p) => ({ ...p, id: p.profile_id, _status: null, _actions: null }));

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        boxShadow: '0 4px 24px rgba(15,45,94,0.07)',
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        localeText={{ ...enUS.components.MuiDataGrid.defaultProps.localeText, noRowsLabel: t('common.no_rows'), noResultsOverlayLabel: t('common.no_rows'), MuiTablePagination: { labelRowsPerPage: t('common.rows_per_page') } }}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        slots={{ toolbar: CustomToolbar }}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 20 } },
          sorting: { sortModel: [{ field: 'lastname', sort: 'asc' }, { field: 'firstname', sort: 'asc' }] },
        }}
        pageSizeOptions={[10, 20, 50]}
        autoHeight
        getRowHeight={() => 'auto'}
        getRowClassName={(params) => {
          if (params.row.is_deleted) return 'row-deleted';
          if (!params.row.is_registered) return 'row-inactive';
          return '';
        }}
        sx={{
          border: 'none',
          fontFamily: '"Outfit", sans-serif',
          '& .MuiDataGrid-columnHeaders': {
            background: `linear-gradient(180deg, ${NAV_BG} 0%, #163571 100%)`,
            borderBottom: 'none',
            borderRadius: 0,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 600,
            fontSize: '0.8rem',
            letterSpacing: '0.04em',
            color: 'rgba(241,245,249,0.85)',
          },
          '& .MuiDataGrid-columnHeader': {
            '&:focus, &:focus-within': { outline: 'none' },
          },
          '& .MuiDataGrid-columnSeparator': {
            color: 'rgba(245,158,11,0.2)',
          },
          '& .MuiDataGrid-row': {
            transition: 'background 0.15s ease',
            '&:hover': { background: 'rgba(15,45,94,0.03)' },
          },
          '& .row-deleted': {
            opacity: 0.5,
            background: '#fafafa',
            '&:hover': { background: '#f5f5f5' },
          },
          '& .row-inactive': {
            background: '#fffbeb',
            '&:hover': { background: '#fef9e7' },
          },
          '& .MuiDataGrid-cell': {
            fontFamily: '"Outfit", sans-serif',
            fontSize: '0.875rem',
            color: '#1e293b',
            borderColor: '#f1f5f9',
            '&:focus, &:focus-within': { outline: 'none' },
            alignItems: 'center',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid #e2e8f0',
            background: '#fafbfc',
          },
          '& .MuiTablePagination-root': {
            fontFamily: '"Outfit", sans-serif',
            fontSize: '0.85rem',
            color: '#475569',
          },
        }}
      />
    </Paper>
  );
};

export default ParentsComponent;
